import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Tag,
  Divider,
  message,
  Spin,
  Tabs,
} from 'antd';
import {
  ArrowLeftOutlined,
  ShopOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { Store } from '../../types/storeGroup';
import StoreGroupService from '../../services/storeGroupService';
import { warehouseService } from '../../services/warehouseService';
import PlanogramList from './components/PlanogramList';

const { TabPane } = Tabs;

const StoreDetail: React.FC = () => {
  const { storeGroupCode, storeId } = useParams<{
    storeGroupCode: string;
    storeId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [store, setStore] = useState<Store | null>(null);
  const [storeGroupName, setStoreGroupName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const storeGroupService = StoreGroupService;

  // 将 warehouse 数据转换为 Store 格式的辅助函数
  const convertWarehouseToStore = (warehouse: any): Store => {
    return {
      id: warehouse.id ? warehouse.id.toString() : '',
      code: warehouse.code || '',
      name: warehouse.name || '',
      address: warehouse.detailAddress || warehouse.address || '',
      region: warehouse.area || warehouse.region || '',
      city: warehouse.city || '',
      type: warehouse.format || warehouse.type || 'Standard',
      size: warehouse.size || 0,
      contact: warehouse.contactName || warehouse.contact || '',
      phone: warehouse.contactPhone || warehouse.phone || '',
      status: warehouse.status === 'ACTIVE' ? 'Active' : 
              warehouse.status === 'INACTIVE' ? 'Inactive' : 
              warehouse.status || 'Active'
    };
  };

  useEffect(() => {
    if (storeGroupCode && storeId) {
      fetchStoreDetail();
    }
  }, [storeGroupCode, storeId]);

  const fetchStoreDetail = async () => {
    setLoading(true);
    try {
      // Get store group information
      const response = await storeGroupService.getStoreGroupByCode(storeGroupCode!);
      if (response.success && response.data) {
        const storeGroup = response.data;
        setStoreGroupName(storeGroup.name);

        // Check if store ID is in the store group's shopCodes
        if (storeGroup.shopCodes && storeGroup.shopCodes.includes(storeId!)) {
          // Get all stores, then filter out stores belonging to the current store group
          const storesResponse = await warehouseService.listWarehouse({ searchStr: '' });
          if (storesResponse.success && storesResponse.data) {
            // Convert data format
            const allStores = storesResponse.data.map(convertWarehouseToStore);
            const storeData = allStores.find(s => s.id === storeId);
            if (storeData) {
              setStore(storeData);
            } else {
              message.error(t('storeDetail.storeNotFound'));
            }
          } else {
            message.error(storesResponse.errorMessage || t('storeDetail.fetchStoresFailed'));
          }
        } else {
          message.error(t('storeDetail.storeNotInGroup'));
        }
      } else {
        message.error(response.errorMessage || t('storeDetail.fetchStoreGroupFailed'));
      }
    } catch (error) {
      message.error(t('storeDetail.fetchStoreDetailFailed'));
      console.error('Failed to fetch store details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/store-group/${storeGroupCode}`);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!store) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>{t('storeDetail.storeNotFoundOrDeleted')}</h2>
        <Button type="primary" onClick={handleBack}>{t('common.back')}</Button>
      </div>
    );
  }

  return (
    <div className="store-detail">
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('common.back')}
          </Button>
          <h1>{t('storeDetail.title')}</h1>
        </Space>
      </div>

      <Card>
        <Descriptions title={t('storeDetail.basicInfo')} bordered column={2}>
          <Descriptions.Item label={t('storeDetail.storeCode')}>{store.code}</Descriptions.Item>
          <Descriptions.Item label={t('storeDetail.storeName')}>{store.name}</Descriptions.Item>
          <Descriptions.Item label={t('storeDetail.storeGroup')}>
            {storeGroupName} ({storeGroupCode})
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDetail.representativeStore')}>
            {store.isRepresentative ? (
              <Tag color="green">{t('common.yes')}</Tag>
            ) : (
              <Tag color="default">{t('common.no')}</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDetail.city')}>{store.city}</Descriptions.Item>
          <Descriptions.Item label={t('storeDetail.address')} span={2}>
            {store.address || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDetail.contact')}>{store.contact || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('storeDetail.phone')}>{store.phone || '-'}</Descriptions.Item>
        </Descriptions>

        <Divider />

        <Tabs defaultActiveKey="planograms">
          <TabPane
            tab={
              <span>
                <AppstoreOutlined />
                {t('storeDetail.planograms')}
              </span>
            }
            key="planograms"
          >
            <PlanogramList
              storeGroupCode={storeGroupCode!}
              storeId={storeId}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default StoreDetail;